if (!customElements.get('hero-slider')) {
  class HeroSlider extends SwiperComponent {
    constructor() {
      super();
      this.lastActiveIndex = null;
    }

    connectedCallback() {
      // Override parent's connectedCallback
      // We'll use SwiperComponent's init method
      this.swiperOptions = this.buildHeroSliderOptions();
      this.swiperInitialized = false;
      this.section = this.closest('section');
      this.observer = null;

      // Initialize swiper (using SwiperComponent's logic)
      if (this.section && this.section.classList.contains('shopify-section')) {
        this.observer = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.init();
              observer.disconnect();
            }
          });
        });
        this.observer.observe(this.section);
      }

      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', () => this.init());
        window.addEventListener('shopify:section:select', () => this.init());

        setTimeout(() => {
          if (!this.swiperInitialized) this.init();
        }, 100);
      } else {
        window.addEventListener('load', () => this.init());
      }

      // Hero-specific event listeners
      window.addEventListener('shopify:block:select', e => {
        if (this.swiper && e.target.closest('hero-slider') === this) {
          const selectedSlideIndex = +e.target.dataset.index;
          this.swiper.slideTo(selectedSlideIndex, 600);
        }
      });

      this.addKeyboardNavigation();

      // Re-evaluate autoplay when crossing the 768px mobile/desktop breakpoint
      this.autoplayMediaQuery = window.matchMedia('(max-width: 767px)');
      this.handleAutoplayBreakpoint = () => this.applyResponsiveAutoplay();
      this.autoplayMediaQuery.addEventListener('change', this.handleAutoplayBreakpoint);
    }

    disconnectedCallback() {
      if (super.disconnectedCallback) super.disconnectedCallback();
      if (this.autoplayMediaQuery && this.handleAutoplayBreakpoint) {
        this.autoplayMediaQuery.removeEventListener('change', this.handleAutoplayBreakpoint);
      }
    }

    /**
     * Resolve the autoplay state for the current viewport.
     * Desktop (>=768px) uses data-autoplay / data-autoplay-interval.
     * Mobile (<768px) uses data-autoplay-mobile / data-autoplay-interval-mobile,
     * falling back to the desktop values if the mobile attributes are absent.
     */
    getEffectiveAutoplay() {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      const desktopEnabled = this.dataset.autoplay === 'true';
      const desktopDelay = parseInt(this.dataset.autoplayInterval, 10) || 5000;
      const hasMobile = this.dataset.autoplayMobile !== undefined;
      const mobileEnabled = hasMobile ? this.dataset.autoplayMobile === 'true' : desktopEnabled;
      const mobileDelay = parseInt(this.dataset.autoplayIntervalMobile, 10) || desktopDelay;
      return {
        enabled: isMobile ? mobileEnabled : desktopEnabled,
        delay: isMobile ? mobileDelay : desktopDelay
      };
    }

    /**
     * Apply the current viewport's autoplay state to the running swiper.
     * Accepts the swiper instance directly (the `init` event fires before
     * this.swiper is assigned).
     */
    applyResponsiveAutoplay(swiperArg) {
      const swiper = swiperArg || this.swiper;
      if (!swiper || !swiper.autoplay) return;
      const { enabled, delay } = this.getEffectiveAutoplay();
      if (swiper.params.autoplay && typeof swiper.params.autoplay === 'object') {
        swiper.params.autoplay.delay = delay;
      } else {
        swiper.params.autoplay = { delay, disableOnInteraction: false };
      }
      if (enabled) {
        swiper.autoplay.start();
      } else {
        swiper.autoplay.stop();
      }
    }

    buildHeroSliderOptions() {
      const effective = this.getEffectiveAutoplay();

      return {
        rewind: true,
        slidesPerView: 1,
        speed: 600,
        followFinger: false,
        navigation: {
          nextEl: '.swiper-button--next',
          prevEl: '.swiper-button--prev'
        },
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          renderBullet: function (i, className) {
            return `
              <button class="${className}">
                <span>0${i + 1}</span>
                <svg class="square-progress" width="26" height="26">
                  <rect class="square-origin" width="26" height="26" rx="5" ry="5" />
                </svg>
                <svg class="progress" width="1.8rem" height="1.8rem" style="inset-inline-start: ${0 - (i * 2.4 + 3.4)}rem">
                  <circle class="circle-origin" r="0.8rem" cx="0.95rem" cy="0.95rem"></circle>
                </svg>
              </button>
            `;
          }
        },
        // Always initialise the autoplay module so it can be toggled per viewport
        // on resize; the init hook immediately stops it when the current
        // viewport's interval is 0.
        autoplay: { delay: effective.delay, disableOnInteraction: false },
        on: {
          init: swiper => {
            this.applyResponsiveAutoplay(swiper);
            this.handleSlideChange(swiper);
          },
          slideChange: this.handleSlideChange.bind(this)
        }
      };
    }

    init() {
      if (this.swiperInitialized) return;
      this.swiperInitialized = true;

      // Use custom Swiper options for HeroSlider
      this.swiper = new Swiper(this, this.swiperOptions);

      // Maintain slider property for backward compatibility
      this.slider = this.swiper;
    }

    handleSlideChange(swiper) {
      this.handleSlideChangeAnimations(swiper);

      /** change header menu color for active slide */
      let headerInner = document.querySelector('.header__inner');
      let heroInners = document.querySelectorAll('.hero__inner');

      if (!headerInner || !heroInners) {
        return;
      }

      // change --transparent-header-menu-text-color value on document style attributes
      document.documentElement.style.setProperty(
        '--transparent-header-menu-text-color',
        heroInners[swiper.activeIndex].dataset.headerMenuTextColor
      );
    }
    /**
     * get direction & set custom transitions
     * with delay for content animation items
     * */
    handleSlideChangeAnimations(swiper) {
      let delay = 300;
      const queryElement = `[data-index="${swiper.activeIndex}"]`;
      const queryArray = [...(swiper.wrapperEl.querySelector(queryElement).querySelectorAll('.hero__animation') || [])];
      if (!queryArray.length) {
        return;
      }
      const allowMobileAnimation = swiper.wrapperEl.closest('.hero__content')?.dataset.animationMobile === 'true';
      if (!allowMobileAnimation && DeviceDetector.isMobile()) {
        queryArray.forEach(element => {
          element.classList.remove('in-delay');
        });
        return;
      }
      // set all element in delay
      queryArray.forEach(element => {
        element.classList.add('in-delay');
      });

      // get direction and set transitions
      if (this.lastActiveIndex > swiper.activeIndex) {
        delay = 650 + delay;
        swiper.wrapperEl.style.transitionDuration = '1000ms';
        swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.45, 0.00, 0.15, 0.95)';
      } else if (this.lastActiveIndex < swiper.activeIndex) {
        delay = 1000 + delay;
        swiper.wrapperEl.style.transitionDuration = '1000ms';
        swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.45, 0.00, 0.15, 0.95)';
      }

      // set delay calculated per item
      setTimeout(() => {
        if (this.lastActiveIndex > swiper.activeIndex) {
          delay = 325 + delay;
        } else if (this.lastActiveIndex < swiper.activeIndex) {
          delay = 500 + delay;
        }
        queryArray.forEach((element, index) => {
          if (index === 1) {
            delay = delay + 50;
          } else if (index === 2) {
            delay = delay + 175;
          } else if (index === 3) {
            delay = delay + 300;
          }
          element.classList.remove('in-delay');
        });
      }, delay);

      // set last active global
      this.lastActiveIndex = swiper.activeIndex;
    }
    addKeyboardNavigation() {
      document.addEventListener('keydown', event => {
        if (this.isInViewport()) {
          if (event.key === 'ArrowRight') {
            const nextButton = this.querySelector('.swiper-button--next');
            if (nextButton) {
              nextButton.click();
            }
          }
          if (event.key === 'ArrowLeft') {
            const prevButton = this.querySelector('.swiper-button--prev');
            if (prevButton) {
              prevButton.click();
            }
          }
        }
      });
    }

    isInViewport() {
      const rect = this.getBoundingClientRect();
      return (
        rect.top < window.innerHeight && // top part in viewport
        rect.bottom > 0 && // bottom part in viewport
        rect.left < window.innerWidth && // left part in viewport
        rect.right > 0 // right part in viewport
      );
    }
  }
  customElements.define('hero-slider', HeroSlider);
}
